import { useState, useEffect, useCallback } from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AdminHeader } from "shared/components/common/AdminHeader";
import { Loading } from "shared/components/ui/Loading";

import { Button, Chip, InputAdornment } from "@mui/material"

import MDBox from "components/MDBox";
import MDSelect from "components/MDSelect";
import MDInput from "components/MDInput";
import DataTable from "components/DataTable";
import MDTypography from "components/MDTypography";
import ModalAdminActionTask from "components/ModalAdminActionTask/ModalAdminActionTask";

import ReactPaginate from "react-paginate";
import { useAsyncDebounce } from "react-table";

import { Add, ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import { ReactComponent as Edit } from "components/icon/svg/edit.svg";
import { ReactComponent as Delete } from "components/icon/svg/delete.svg";

import {
  useCreateGeneralTaskMutation,
  useUpdateGeneralTaskMutation,
  useDeleteGeneralTaskMutation,
  useLazyGetGeneralTasksQuery,
  useTriggerCreateSubTaskMutation,
} from "shared/redux/endpoints/general-task";

import {
  GeneralTaskFrequency,
  GeneralTaskStatus,
  DAYS_OF_WEEK,
  DAYS_OF_MONTH,
  STATUS_LIST,
} from "constant/GeneralTaskType";

import moment from "moment";
import { ToastUtil } from "utilities/toastUtil";

const getRecurrenceLabel = (frequency, recurrenceDayValue) => {
  if (!frequency || !recurrenceDayValue) return "";

  if (frequency === GeneralTaskFrequency.WEEKLY) {
    const day = DAYS_OF_WEEK.find((c) => c.value === recurrenceDayValue);
    return day ? `Every ${day.label}` : "";
  }

  if (frequency === GeneralTaskFrequency.MONTHLY) {
    const day = DAYS_OF_MONTH.find((c) => c.value === recurrenceDayValue);
    const ordinal = getOrdinal(day.value);
    return day ? `Every month on day ${ordinal}` : "";
  }

  return "";
};

const getOrdinal = (n) => {
  if (n <= 0) return "";
  const lastDigit = n % 10;
  if (lastDigit === 1 && n !== 11) return `${n}st`;
  if (lastDigit === 2 && n !== 12) return `${n}nd`;
  if (lastDigit === 3 && n !== 13) return `${n}rd`;
  return `${n}th`;
};

// Constants
const categoryList = [
  { label: "Planning", value: "PLANNING" },
  { label: "Scheduling", value: "SCHEDULING" },
  { label: "Reporting", value: "REPORTING" },
];

const frequencyList = [
  { label: "Weekly", value: GeneralTaskFrequency.WEEKLY },
  { label: "Monthly", value: GeneralTaskFrequency.MONTHLY },
];

const initialState = {
  page: 0,
  size: 10,
  roleType: "LXO",
  sort: "created,desc",
};

const tableColumns = [
  { Header: "No", accessor: "id", width: 50 },
  { Header: "Title", accessor: "title" },
  { Header: "Category", accessor: "category", width: 70 },
  { Header: "Frequency", accessor: "frequency" },
  { Header: "From Date", accessor: "fromDate", width: 85 },
  { Header: "To Date", accessor: "toDate", width: 70 },
  { Header: "Start Time", accessor: "startTime", width: 90 },
  { Header: "End Time", accessor: "endTime", width: 80 },
  { Header: "Status", accessor: "status" },
  { Header: "Action", accessor: "action", width: 70, sticky: "right" },
];

export default function LXOTask() {
  const [triggerGeneralTasks, { isLoading: isLoadingGeneralTasks }] =
    useLazyGetGeneralTasksQuery();
  const [createTask] = useCreateGeneralTaskMutation();
  const [updateTask] = useUpdateGeneralTaskMutation();
  const [deleteTask] = useDeleteGeneralTaskMutation();

  const [triggerCreateSubtask] = useTriggerCreateSubTaskMutation();

  const [state, setState] = useState(initialState);
  const [pagination, setPagination] = useState({
    totalPage: 0,
    pageSize: 0,
    currentPage: 0,
    totalElement: 0,
  });
  const [dataTable, setDataTable] = useState({
    columns: tableColumns,
    rows: [],
  });
  const [modalState, setModalState] = useState({
    create: { isOpen: false },
    update: { isOpen: false, item: {} },
    delete: { isOpen: false, item: {} },
  });

  const formatTaskData = useCallback(
    (data) => ({
      title: data.title,
      code: data.code,
      category: data.category?.value,
      frequency: data.frequency?.value,
      fromDate: new Date(data.fromDate).getTime(),
      toDate: new Date(data.toDate).getTime(),
      startTime: new Date(data.startTime).getTime(),
      endTime: new Date(data.endTime).getTime(),
      recurrenceDay: data.recurrenceDay?.value,
      note: data.note,
      status: data.status || GeneralTaskStatus.PENDING,
      roleType: "LXO",
      ...(data.id && { id: data.id }),
    }),
    []
  );

  const showToast = useCallback((status, message) => {
    if (status) {
      ToastUtil.showToastSuccess(message);
    } else {
      ToastUtil.showToastError(message);
    }
  }, []);

  const fetchTasks = useCallback(async (params) => {
    const resp = await triggerGeneralTasks(params);
    if (resp?.data?.data?.data) {
      setDataTable({
        columns: tableColumns,
        rows: resp.data.data.data.map((item) => {
          const categoryLabel = categoryList.find(
            (c) => c.value === item.category
          )?.label;
          const frequencyLabel = frequencyList.find(
            (c) => c.value === item.frequency
          )?.label;

          const isNotAllowedDelete =
            item.status === GeneralTaskStatus.COMPLETE ||
            item.status === GeneralTaskStatus.IN_PROGRESS;

          return {
            ...item,
            title: (
              <MDBox
                onClick={() => handleModalAction(item, "update")}
                sx={{ cursor: "pointer" }}
              >
                <span>{item.title}</span>
                <br />
                <small style={{ color: "#666" }}>{item.code}</small>
              </MDBox>
            ),
            category: categoryLabel,
            frequency: (
              <MDBox>
                <span>{frequencyLabel}</span>
                <br />
                <small style={{ color: "#666", fontSize: "80%" }}>
                  <b>
                    {getRecurrenceLabel(item.frequency, item.recurrenceDay)}
                  </b>
                </small>
              </MDBox>
            ),
            //convert with current timezone from browser
            fromDate: moment.utc(item.fromDate).local().format("DD/MM/YYYY"),
            toDate: moment.utc(item.toDate).local().format("DD/MM/YYYY"),
            startTime: moment.utc(item.startTime).local().format("HH:mm"),
            endTime: moment.utc(item.endTime).local().format("HH:mm"),
            status: (
              <Chip
                className={`chip-status chip-status-${item.status}`}
                sx={{
                  cursor: "default",
                  background:
                    STATUS_LIST.find((s) => s.value === item.status)
                      ?.background || "#aaa",
                  color:
                    STATUS_LIST.find((s) => s.value === item.status)?.color ||
                    "#fff",
                }}
                label={
                  STATUS_LIST.find((s) => s.value === item.status)?.label || ""
                }
              />
            ),
            action: (
              <MDBox className="action-icon">
                <Edit
                  className="action-icon"
                  onClick={() => handleModalAction(item, "update")}
                />
                <Delete
                  className="action-icon"
                  style={
                    isNotAllowedDelete
                      ? { filter: "opacity(0.5)", cursor: "not-allowed" }
                      : null
                  }
                  onClick={() => {
                    if (!isNotAllowedDelete) handleModalAction(item, "delete");
                  }}
                />
              </MDBox>
            ),
          };
        }),
      });
      setPagination({
        totalPage: resp.data.data.totalPage,
        pageSize: resp.data.data.pageSize,
        currentPage: resp.data.data.currentPage,
        totalElement: resp.data.data.totalElement,
      });
      setState((prev) => ({ ...prev, ...params }));
    }
  }, []);

  const handleModalAction = useCallback((item, action) => {
    switch (action) {
      case "delete":
        setModalState((prev) => ({ ...prev, delete: { isOpen: true, item } }));
        break;
      case "update":
        setModalState((prev) => ({ ...prev, update: { isOpen: true, item } }));
        break;
      case "create":
        setModalState((prev) => ({ ...prev, create: { isOpen: true } }));
        break;
      default:
        break;
    }
  }, []);

  const handleSearch = useAsyncDebounce((value) => {
    fetchTasks({ ...state, keyword: value?.trim() || "", page: 0 });
  }, 1000);

  const handleFilterChange = useCallback(
    (key, e) => {
      fetchTasks({ ...state, [key]: e?.value, page: 0 });
    },
    [state, fetchTasks]
  );

  const handlePageChange = useCallback(
    (page) => {
      fetchTasks({ ...state, page });
    },
    [state, fetchTasks]
  );

  const dataTableProps = {
    className: "table",
    table: dataTable,
    entriesPerPage: false,
    showTotalEntries: false,
    isSorted: false,
  };

  const paginationProps = {
    breakLabel: "...",
    nextLabel: <ChevronRight />,
    previousLabel: <ChevronLeft />,
    previousClassName: "paginate-general-task-item",
    nextClassName: "paginate-general-task-item",
    forcePage: Number(state.page),
    onPageChange: (event) => handlePageChange(event.selected),
    pageRangeDisplayed: 1,
    pageCount: pagination.totalPage,
    renderOnZeroPageCount: null,
  };

  const modalProps = {
    create: {
      isOpen: modalState.create.isOpen,
      onClose: () =>
        setModalState((prev) => ({ ...prev, create: { isOpen: false } })),
      action: "create",
      category: categoryList,
      frequency: frequencyList,
      onSubmit: (data) => {
        createTask(formatTaskData(data)).then((res) => {
          showToast(
            res?.data?.status,
            `Create task ${res?.data?.status ? "successfully" : "failed"}`
          );
          fetchTasks(initialState);
          setModalState((prev) => ({ ...prev, create: { isOpen: false } }));
        });
      },
    },
    update: {
      isOpen: modalState.update.isOpen,
      onClose: () =>
        setModalState((prev) => ({
          ...prev,
          update: { isOpen: false, item: {} },
        })),
      action: "update",
      item: modalState.update.item,
      category: categoryList,
      frequency: frequencyList,
      triggerUpdateStatus: triggerCreateSubtask,
      onSubmit: (data) => {
        updateTask(formatTaskData(data)).then((res) => {
          showToast(
            res?.data?.status,
            `Update task ${res?.data?.status ? "successfully" : "failed"}`
          );

          if (res?.data?.data?.status === GeneralTaskStatus.IN_PROGRESS) {
            triggerCreateSubtask(data.id);
          }

          fetchTasks(initialState);
          setModalState((prev) => ({
            ...prev,
            update: { isOpen: false, item: {} },
          }));
        });
      },
    },
    delete: {
      isOpen: modalState.delete.isOpen,
      onClose: () =>
        setModalState((prev) => ({
          ...prev,
          delete: { isOpen: false, item: {} },
        })),
      action: "delete",
      item: modalState.delete.item,
      onSubmit: (data) => {
        deleteTask(data).then((res) => {
          showToast(
            res?.data?.status,
            `Delete task ${res?.data?.status ? "successfully" : "failed"}`
          );

          fetchTasks(initialState);
          setModalState((prev) => ({
            ...prev,
            delete: { isOpen: false, item: {} },
          }));
        });
      },
    },
  };

  useEffect(() => {
    fetchTasks(initialState);
  }, [fetchTasks]);

  return (
    <DashboardLayout>
      <AdminHeader />
      <DashboardNavbar light />
      {isLoadingGeneralTasks ? (
        <MDBox className="container-lxo-task">
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height={300}
          >
            <Loading style={{ width: 150, height: 100 }} />
          </MDBox>
        </MDBox>
      ) : (
        <MDBox className="container-lxo-task">
          <MDBox className="header-lxo-task">
            <MDBox className="group-title">
              <MDTypography className="title">LXO Task</MDTypography>
            </MDBox>
            <MDBox className="group-btn">
              <Button
                className="btn btn-create"
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleModalAction(null, "create")}
              >
                Create Task
              </Button>
            </MDBox>
          </MDBox>
          <MDBox className="content-lxo-task">
            <MDBox className="group-filter">
              <MDInput
                className="input input-search input-search-text"
                placeholder="Search here..."
                startAdornment={
                  <InputAdornment position="start">
                    <Search className="icon-search" />
                  </InputAdornment>
                }
                onChange={({ currentTarget }) =>
                  handleSearch(currentTarget.value)
                }
              />
              <MDSelect
                className="input input-search input-search-select"
                options={[
                  { value: "", label: "Select category" },
                  ...categoryList,
                ]}
                value={
                  categoryList.find((item) => item.value === state.category) ||
                  ""
                }
                onChange={(e) => handleFilterChange("category", e)}
                placeholder="Select category"
              />
              <MDSelect
                className="input input-search input-search-select"
                options={[
                  { value: "", label: "Select frequency" },
                  ...frequencyList,
                ]}
                value={
                  frequencyList.find(
                    (item) => item.value === state.frequency
                  ) || ""
                }
                onChange={(e) => handleFilterChange("frequency", e)}
                placeholder="Select frequency"
              />
            </MDBox>
            <MDBox className="group-table">
              <DataTable {...dataTableProps} />
              <MDBox className="table-footer">
                <MDBox className="table-footer-showing">
                  Showing {dataTable.rows.length} of {pagination.totalElement}{" "}
                  entries
                </MDBox>
                {pagination.totalElement > 0 && (
                  <MDBox className="pagination">
                    <ReactPaginate {...paginationProps} />
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      )}
      {modalState.create.isOpen && (
        <ModalAdminActionTask {...modalProps.create} />
      )}
      {modalState.update.isOpen && (
        <ModalAdminActionTask {...modalProps.update} />
      )}
      {modalState.delete.isOpen && (
        <ModalAdminActionTask {...modalProps.delete} />
      )}
    </DashboardLayout>
  );
}
